import { Injectable, inject } from '@angular/core';
import { BoxDetection } from '@models';
import {
  DetectionService,
  MessageService,
  MessagesToAIService,
} from '@services';
import '@tensorflow/tfjs-backend-webgl';
import { AnatomoMetricsService } from './anatomo-metrics.service';
import { AiXpertService } from '@services';

const TIME_TO_WAIT_FOR_MESSAGES = 10000;

@Injectable({
  providedIn: 'root',
})
export class SharedService {
  #aiXpert = inject(AiXpertService);
  private detectionService = inject(DetectionService);
  private messageService = inject(MessageService);
  private anatomoMetricsService = inject(AnatomoMetricsService);
  private messagesToAIService = inject(MessagesToAIService);
  cycleWorkerId: string = undefined;
  piercingBackStitchWorkerId: string = undefined;
  suturingPhasesClassificationWorkerId: string = undefined;
  suturingMistakesClassificationWorkerId: string = undefined;

  sutureInTissueOccurencies = 0;
  needleInTissueOccurencies = 0;
  positionReverseStitching = { x: 0, y: 0 };
  isToShowFirstReverseStitchResult = true;
  distanceNeedleTool: number = null;
  originalWound: BoxDetection = null;
  lastDetectedOpenWound: BoxDetection = null;
  messageEveryTenSeconds = '';
  private messageLogTimer: ReturnType<typeof setInterval>;
  allMessagesLog = ''; // ✅ Will store everything over time
  isLoopAroundToolDetected = false;
  isKnotTyingDetected = false;

  constructor() {
    this.startMessageResetTimer();
  }

  private checkAndAppend(condition: boolean, message: string) {
    if (condition) this.messageEveryTenSeconds += `${message}\n`;
  }

  messageGeneration() {
    const getTrendSuturingPhases = (
      key: string,
      threshold: number,
      window: number,
    ) =>
      this.detectionService.getRegressionTrend(
        key,
        this.suturingPhasesClassificationWorkerId,
        window,
        threshold,
      );

    const getTrendSuturingMistakes = (
      key: string,
      threshold: number,
      window: number,
    ) =>
      this.detectionService.getRegressionTrend(
        key,
        this.suturingMistakesClassificationWorkerId,
        window,
        threshold,
      );

    const phaseMessages = this.messagesToAIService.phaseMessages();
    const mistakeMessages = this.messagesToAIService.mistakeMessages();

    for (const { key, msg, threshold, window } of phaseMessages) {
      const detected = getTrendSuturingPhases(key, threshold, window);
      this.checkAndAppend(detected, msg);
    }
    if (!getTrendSuturingPhases('nothing', 0.4, 3)) {
      for (const { key, msg, threshold, window } of mistakeMessages) {
        const detected = getTrendSuturingMistakes(key, threshold, window);
        this.checkAndAppend(detected, msg);
      }
    }
  }

  startMessageResetTimer() {
    this.messageLogTimer = setInterval(() => {
      if (this.messageEveryTenSeconds) {
        // if (!this.appService.debugMode()) { // to donwload log file
        //   this.downloadLogFile();
        // }
        // Send message to ChatGPT (implement this method yourself)
        this.sendToChatGPT(this.messageEveryTenSeconds.trim());
        this.allMessagesLog += this.messageEveryTenSeconds.trim() + '\n\n';
        this.messageEveryTenSeconds = '';
      }
    }, TIME_TO_WAIT_FOR_MESSAGES);
  }

  public downloadLogFile() {
    const blob = new Blob([this.allMessagesLog], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'surgical-feedback-log.txt';
    a.click();
    URL.revokeObjectURL(url);
  }

  appendMessage(text: string) {
    this.messageEveryTenSeconds += `${text}\n`;
  }

  private sendToChatGPT(message: string) {
    this.#aiXpert.sendXperMessage(message);
  }

  prepareMaterials(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        const isIpadWrongPositioned =
          this.detectionService.getRegressionTrend(
            'nothing',
            this.suturingPhasesClassificationWorkerId,
            3,
          ) ||
          this.detectionService.regressionHistory
            .get(this.suturingPhasesClassificationWorkerId)
            ?.get('nothing') === undefined;

        const isTissueWithoutWound =
          this.detectionService.getRegressionTrend(
            'tissueWithoutWound',
            this.suturingPhasesClassificationWorkerId,
            3,
          ) ||
          this.detectionService.regressionHistory
            .get(this.suturingPhasesClassificationWorkerId)
            ?.get('tissueWithoutWound') === undefined;

        const isIpadAndTissuePrepared =
          !isIpadWrongPositioned && !isTissueWithoutWound;

        if (isIpadAndTissuePrepared) {
          this.messageService.showDetectionMessage({
            text: 'The iPad is placed, tissue with wound is presented.',
            identifier: 'prepareMaterialsConfirmed',
          });
        } else {
          this.messageService.showDetectionMessage({
            text: 'Place the iPad and prepare a tissue with vertical wound.',
            identifier: 'prepareMaterials',
          });
        }
        resolve(isIpadAndTissuePrepared);
      } catch (error) {
        reject(error);
      }
    });
  }

  fixation(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        const isSuturePlaced = this.detectionService.getRegressionTrend(
          'firstSuturePlacement',
          this.suturingPhasesClassificationWorkerId,
          3,
          0.7,
        );

        if (isSuturePlaced) {
          this.messageService.showDetectionMessage({
            text: 'Tie the knot to secure the suture',
            identifier: 'takeFirstPassConfirmed',
          });
        } else {
          this.messageService.showDetectionMessage({
            text: 'Take the first pass in a direction away from the incision',
            identifier: 'takeFirstPass',
          });
        }
        resolve(isSuturePlaced);
      } catch (error) {
        reject(error);
      }
    });
  }

  tieKnot(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        const isKnotTyingDetected = this.detectionService.getRegressionTrend(
          'knotTying',
          this.suturingPhasesClassificationWorkerId,
          3,
          0.7,
        );

        const isLoopAroundToolDetected =
          this.detectionService.getRegressionTrend(
            'loopAroundTool',
            this.suturingPhasesClassificationWorkerId,
            3,
            0.7,
          );

        const isTightKnotDetected = this.detectionService.getRegressionTrend(
          'knotTight',
          this.suturingMistakesClassificationWorkerId,
          3,
          0.7,
        );

        if (isLoopAroundToolDetected) {
          this.isLoopAroundToolDetected = true;
        }

        if (isKnotTyingDetected) {
          this.isKnotTyingDetected = true;
        }

        const isKnotTyingDone =
          isTightKnotDetected &&
          (this.isKnotTyingDetected || this.isLoopAroundToolDetected);

        if (isKnotTyingDone) {
          this.messageService.showDetectionMessage({
            text: 'Make sure the knot is tight and cut the suture',
            identifier: 'tieKnotConfirmed',
          });
        } else {
          this.messageService.showDetectionMessage({
            text: 'Place the loop around the tool and tie the knot',
            identifier: 'tieKnot',
          });
        }
        resolve(isKnotTyingDone);
      } catch (error) {
        reject(error);
      }
    });
  }

  lockStitch(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        const isStitchLockingDetected =
          this.detectionService.getRegressionTrend(
            'stitchSecure',
            this.suturingPhasesClassificationWorkerId,
            3,
            0.4,
          );

        this.messageService.showDetectionMessage({
          text: 'Take a pass in the intact tissue perpendicular to the initial pass to lock the stitch',
          identifier: 'lockStitchProgress',
        });

        if (isStitchLockingDetected) {
          this.messageService.showDetectionMessage({
            text: 'Now close the wound',
            identifier: 'lockStitchConfirmed',
          });
        } else {
          this.messageService.showDetectionMessage({
            text: 'Take the first pass in a direction away from the incision',
            identifier: 'lockStitch',
          });
        }
        resolve(isStitchLockingDetected);
      } catch (error) {
        reject(error);
      }
    });
  }

  closeWoundInProgress(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        const isClosingWoundInProgressDetected =
          this.detectionService.getRegressionTrend(
            'halfWoundClosed',
            this.suturingPhasesClassificationWorkerId,
            3,
            0.9,
          );

        this.messageService.showDetectionMessage({
          text: 'Keep closing the rest of the wound',
          identifier: 'lockStitch',
        });

        if (isClosingWoundInProgressDetected) {
          this.messageService.showDetectionMessage({
            text: 'Part of the wound is closed. Keep closing the rest of the wound',
            identifier: 'woundInProgressConfirmed',
          });
        } else {
          this.messageService.showDetectionMessage({
            text: 'Take the first pass in a direction away from the incision',
            identifier: 'woundInProgress',
          });
        }
        resolve(isClosingWoundInProgressDetected);
      } catch (error) {
        reject(error);
      }
    });
  }

  closeWound(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        const isClosedWoundDetected = this.detectionService.getRegressionTrend(
          'wholeWoundClosed',
          this.suturingPhasesClassificationWorkerId,
          3,
          0.9,
        );

        if (isClosedWoundDetected) {
          this.messageService.showDetectionMessage({
            text: 'You closed the wound. Cut the suture now.',
            identifier: 'closingWoundConfirmed',
          });
        } else {
          this.messageService.showDetectionMessage({
            text: 'Keep closing the wound, pierce the tissue from right to left',
            identifier: 'closingWound',
          });
        }
        resolve(isClosedWoundDetected);
      } catch (error) {
        reject(error);
      }
    });
  }

  cutSuture(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        const isCuttingSuture = this.detectionService.getRegressionTrend(
          'sutureCut',
          this.suturingPhasesClassificationWorkerId,
          3,
          0.9,
        );

        if (isCuttingSuture) {
          this.messageService.showDetectionMessage({
            text: 'You cut the suture. Well done!',
            identifier: 'cutSutureConfirmed',
          });
        } else {
          this.messageService.showDetectionMessage({
            text: 'Cut the suture',
            identifier: 'cutSuture',
          });
        }
        resolve(isCuttingSuture);
      } catch (error) {
        reject(error);
      }
    });
  }

  toolTracking() {
    this.anatomoMetricsService.trackLeftTool(
      this.detectionService.getBox('toolLeft', this.cycleWorkerId),
    );
    this.anatomoMetricsService.trackRightTool(
      this.detectionService.getBox('toolRight', this.cycleWorkerId),
    );
  }
}
